/* eslint-disable react/prop-types */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import React, {
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";

import { format, subDays } from "date-fns";
import { toast } from "react-toastify";

import {
  BillData,
  FilterParams,
  GetBillsResponse,
  Unit,
  billDownload,
  getBills,
  getUnits,
  sendAnswers,
} from "../services/api";

interface Answer {
  questionId: string;
  answer: string;
}

export interface RequestPoolData {
  document: string;
  answers: Answer[];
  siteId: string;
}

interface BillSessionDTO {
  document: string;
  setDocument: Dispatch<SetStateAction<string>>;
  bills: GetBillsResponse["bills"];
  currentPage: number;
  setCurrentPage: Dispatch<SetStateAction<number>>;
  totalPages?: number;
  setTotalPages?: Dispatch<SetStateAction<number>>;
  step?: number;
  setStep?: Dispatch<SetStateAction<number>>;
  units: Unit[];
  hasOneUnit?: boolean;
  setHasOneUnit?: Dispatch<SetStateAction<boolean>>;
  selectedUnit?: Unit;
  setSelectedUnit?: Dispatch<SetStateAction<Unit>>;
  customFilter?: string;
  setCustomFilter?: Dispatch<SetStateAction<string>>;
  beginDate?: string;
  setBeginDate?: Dispatch<SetStateAction<string>>;
  endDate?: string;
  setEndDate?: Dispatch<SetStateAction<string>>;
  handleCustomFilter?: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  status?: string;
  setStatus?: Dispatch<SetStateAction<string>>;
  loading?: boolean;
  setLoading?: Dispatch<SetStateAction<boolean>>;
  requestPool?: RequestPoolData | null;
  setRequestPool?: Dispatch<SetStateAction<RequestPoolData>>;
  poolToken?: string | null;
  setPoolToken?: Dispatch<SetStateAction<string | null>>;
  condominiumName?: string;
  setCondominiumName?: Dispatch<SetStateAction<string>>;
  unitName?: string;
  setUnitName?: Dispatch<SetStateAction<string>>;
  requestToken?: () => Promise<string | null>;
  handleClearData?: () => void;
  billError?: boolean;
  handleDownloadBill?: (
    isReduced: boolean,
    token: string,
    invoice: BillData,
    installment: {
      paymentTypeBoleto: boolean;
    } & BillData
  ) => Promise<void>;
  selectedInvoice: BillData;
  setSelectedInvoice: Dispatch<SetStateAction<BillData>>;
  selectedInstallment: {
    paymentTypeBoleto: boolean;
  } & BillData;
  setSelectedInstallment: Dispatch<
    SetStateAction<
      {
        paymentTypeBoleto: boolean;
      } & BillData
    >
  >;
}

const QuickBillContext = createContext<BillSessionDTO>({} as BillSessionDTO);

export const QuickBillProvider = ({ children }) => {
  const [document, setDocument] = useState("");
  const [bills, setBills] = useState<GetBillsResponse["bills"]>();
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [step, setStep] = useState(1);
  const [units, setUnits] = useState<Unit[]>([]);
  const [selectedUnit, setSelectedUnit] = useState<Unit>({} as Unit);
  const [customFilter, setCustomFilter] = useState<string>("");
  const [beginDate, setBeginDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [order, setOrder] = useState<string>("desc");
  const [status, setStatus] = useState<string>("TODOS");
  const [loading, setLoading] = useState(false);
  const [requestPool, setRequestPool] = useState<RequestPoolData>();
  const [billError, setBillError] = useState(false);
  const [hasOneUnit, setHasOneUnit] = useState(false);
  const [poolToken, setPoolToken] = useState<string | null>(null);
  const [unitName, setUnitName] = useState("");
  const [condominiumName, setCondominiumName] = useState("");
  const [selectedInvoice, setSelectedInvoice] = useState<BillData>(
    {} as BillData
  );
  const [selectedInstallment, setSelectedInstallment] = useState<
    {
      paymentTypeBoleto: boolean;
    } & BillData
  >({} as { paymentTypeBoleto: boolean } & BillData);

  const handleCustomFilter = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = event.target;
    setCustomFilter(value);
    setCurrentPage(1);
    if (value === "old15days") {
      const beginDate = format(subDays(new Date(), 15), "yyyy-MM-dd");
      const endDate = format(new Date(), "yyyy-MM-dd");
      setBeginDate(beginDate);
      setEndDate(endDate);
    } else if (value === "old30days") {
      const beginDate = format(subDays(new Date(), 30), "yyyy-MM-dd");
      const endDate = format(new Date(), "yyyy-MM-dd");
      setBeginDate(beginDate);
      setEndDate(endDate);
    } else if (value === "old60days") {
      const beginDate = format(subDays(new Date(), 60), "yyyy-MM-dd");
      const endDate = format(new Date(), "yyyy-MM-dd");
      setBeginDate(beginDate);
      setEndDate(endDate);
    } else if (value === "old90days") {
      const beginDate = format(subDays(new Date(), 90), "yyyy-MM-dd");
      const endDate = format(new Date(), "yyyy-MM-dd");
      setBeginDate(beginDate);
      setEndDate(endDate);
    } else {
      if (value === "recentToOld") {
        setOrder("");
      } else if (value === "oldToRecent") {
        setOrder("asc");
      }
      setBeginDate("");
      setEndDate("");
    }
  };

  const requestToken = async (): Promise<string | null> => {
    if (
      !requestPool?.document ||
      !requestPool?.answers ||
      !requestPool?.siteId
    ) {
      return null;
    }
    return sendAnswers(requestPool);
  };

  useEffect(() => {
    async function getUnitsData(): Promise<void> {
      try {
        const response = await getUnits(document);
        setUnits(response);
        if (response.length > 0) {
          if (response.length === 1) {
            setHasOneUnit(true);
            setSelectedUnit(response[0]);
          }
          setCurrentPage(1);
        }
      } catch (error) {
        toast.error("Erro ao buscar unidades");
      }
    }

    if (document) {
      getUnitsData();
    }
  }, [document]);

  useEffect(() => {
    setCurrentPage(1);
  }, [status, selectedUnit]);

  useEffect(() => {
    async function getData(): Promise<void> {
      setLoading(true);
      const filters: FilterParams = {
        beginDate,
        endDate,
        order: order ? `dueDate ${order}` : "status, dueDate desc",
        status: status === "TODOS" ? "" : status,
        condominiumName,
        unitName,
      };

      const tokenRequest = (await requestToken()) || poolToken;

      try {
        const { bills: billsData, pageTotal } = await getBills(
          document,
          selectedUnit?.instanceId,
          currentPage,
          filters,
          tokenRequest
        );
        setBills(billsData);
        setTotalPages(pageTotal);
      } catch (error) {
        setBillError(true);
        console.error(error);
      } finally {
        setLoading(false);
      }
    }

    if (document && poolToken) {
      getData();
    }
  }, [
    document,
    selectedUnit,
    currentPage,
    beginDate,
    endDate,
    order,
    status,
    poolToken,
    unitName,
    condominiumName,
  ]);

  useEffect(() => {
    async function tokenPoll() {
      const token = await requestToken();
      setPoolToken(token);
    }
    tokenPoll();
  }, []);

  const handleDownloadBill = async (
    isReduced: boolean,
    token: string,
    invoice,
    installment
  ): Promise<void> => {
    await billDownload(isReduced, invoice, installment, token);
  };

  const handleClearData = (): void => {
    setDocument("");
    setBills([]);
    setCurrentPage(1);
    setTotalPages(0);
    setStep(1);
    setUnits([]);
    setSelectedUnit({} as Unit);
    setCustomFilter("");
    setBeginDate("");
    setEndDate("");
    setOrder("desc");
    setStatus("TODOS");
    setLoading(false);
    setRequestPool(undefined);
    setPoolToken(null);
    setSelectedInvoice({} as BillData);
    setSelectedInstallment({} as { paymentTypeBoleto: boolean } & BillData);
    setHasOneUnit(false);
    setUnitName("");
    setCondominiumName("");
  };

  return (
    <QuickBillContext.Provider
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        units,
        bills,
        setDocument,
        document,
        currentPage,
        setCurrentPage,
        totalPages,
        setTotalPages,
        step,
        setStep,
        selectedUnit,
        setSelectedUnit,
        handleCustomFilter,
        customFilter,
        status,
        setStatus,
        loading,
        setLoading,
        requestPool,
        setRequestPool,
        requestToken,
        handleDownloadBill,
        selectedInvoice,
        setSelectedInvoice,
        selectedInstallment,
        setSelectedInstallment,
        handleClearData,
        hasOneUnit,
        billError,
        poolToken,
        setPoolToken,
        unitName,
        setUnitName,
        condominiumName,
        setCondominiumName,
      }}
    >
      {children}
    </QuickBillContext.Provider>
  );
};

export const useQuickBillContext = () => useContext(QuickBillContext);
